/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Left pane — phase-grouped WBS outline tree for the Task Builder.
 */

import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import { Task, PHASES, PhaseId } from '../types';
import { taskDepth, isMilestoneUid } from '../model';

export interface TaskOutlineTreeProps {
  tasks: Task[];               // pass only valid && status==='active' tasks in
  selectedUid: string | null;
  onSelect: (uid: string) => void;
  onAddTask: (phase: PhaseId) => void;   // auto-numbers under that phase's root, e.g. "2.4"
  onAddChild: (parentUid: string) => void;
  onInsertAfter: (afterUid: string) => void;   // new sibling slotted right after this task, not nested under it
}

/** Left pane: phase-grouped WBS outline tree. */
export default function TaskOutlineTree({ tasks, selectedUid, onSelect, onAddTask, onAddChild, onInsertAfter }: TaskOutlineTreeProps) {
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const byPhase = useMemo(() => {
    const m: Record<string, Task[]> = {};
    PHASES.forEach(p => (m[p.id] = []));
    tasks.filter(t => !filter || t.taskName.toLowerCase().includes(filter.toLowerCase()))
      .forEach(t => (m[t.phase] ??= []).push(t));
    Object.values(m).forEach(arr => arr.sort((a, b) => a.task_order - b.task_order));
    return m;
  }, [tasks, filter]);

  return (
    <Box sx={{ width: '100%', height: '100%', borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth size="small" placeholder="Search tasks…" value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 0.75 }}>
        {PHASES.map(phase => {
          const list = byPhase[phase.id] || [];
          const isCollapsed = collapsed[phase.id];
          return (
            <Box key={phase.id} sx={{ mb: 0.5 }}>
              <Box
                onClick={() => setCollapsed(c => ({ ...c, [phase.id]: !c[phase.id] }))}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ExpandMoreIcon fontSize="small" sx={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .1s', color: 'text.secondary' }} />
                <Typography variant="overline" sx={{ flex: 1, color: 'text.secondary', fontWeight: 700 }}>{phase.label}</Typography>
                <Typography variant="caption" color="text.disabled">{list.length}</Typography>
              </Box>
              <Collapse in={!isCollapsed}>
                {list.map(t => {
                  const depth = taskDepth(t.task_uid);
                  const active = t.task_uid === selectedUid;
                  const milestone = isMilestoneUid(tasks, t.task_uid);
                  return (
                    <Box key={t.task_uid} onClick={() => onSelect(t.task_uid)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 1, pl: 2 + depth * 1.75,
                        borderRadius: 1, cursor: 'pointer',
                        bgcolor: active ? (theme => alpha(theme.palette.primary.main, 0.12)) : 'transparent',
                        '&:hover': { bgcolor: active ? (theme => alpha(theme.palette.primary.main, 0.12)) : 'action.hover' } }}
                    >
                      <Box sx={{ width: 7, height: 7, borderRadius: milestone ? 0 : '50%', bgcolor: milestone ? 'primary.main' : 'text.disabled', transform: milestone ? 'rotate(45deg)' : 'none', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>{t.task_uid}</Typography>
                      <Typography variant="body2" noWrap sx={{ flex: 1, color: active ? 'primary.dark' : 'text.primary', fontWeight: active ? 600 : 400 }} title={t.taskName}>
                        {t.taskName}
                      </Typography>
                      {t.dependencies.length > 0 && <LinkIcon fontSize="inherit" sx={{ color: 'secondary.main', flexShrink: 0 }} />}
                      {/* A phase root (depth 0) can't take a new sibling either — it would mint a
                          disconnected 5th "phase" entry (see insertTaskAfter's own depth-0 guard). */}
                      {depth > 0 && (
                        <IconButton size="small" title="Insert task after" onClick={e => { e.stopPropagation(); onInsertAfter(t.task_uid); }} sx={{ p: 0.25 }}>
                          <PlaylistAddIcon fontSize="inherit" />
                        </IconButton>
                      )}
                      <IconButton size="small" title="Add sub-task" onClick={e => { e.stopPropagation(); onAddChild(t.task_uid); }} sx={{ p: 0.25 }}>
                        <AddIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  );
                })}
                <Box component="button" onClick={() => onAddTask(phase.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', border: 'none', background: 'none', cursor: 'pointer',
                    py: 0.5, px: 1, pl: 3.75, borderRadius: 1, color: 'text.secondary', fontSize: 12, '&:hover': { bgcolor: 'action.hover' } }}>
                  <AddIcon fontSize="inherit" /> Add task
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
